// @ts-nocheck
"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { db } from "@/lib/db";
import { getIntegrationsForCurrentUser, getCurrentUserId } from "@/lib/integrations/scope";
import type { IntegrationType } from "@/types/integration";
import type { Integration } from "@/types/integration";
import { INTEGRATION_AUTH_MAP, getIntegrationAuthConfig } from "@/lib/integrations/oauth-mapping";
import { getAvailableIntegrationsFromCapabilities, type AvailableIntegrationRow } from "@/lib/platform/capability-display";
import { type Capability } from "@/lib/platform/capabilities";
import { useTheme } from "@/components/layout/ThemeProvider";
import ProductIcon from "@/components/ui/ProductIcon";
import { 
  Search, 
  ChevronRight, 
  Check, 
  ExternalLink, 
  Key, 
  LogIn, 
  Filter,
  SortAsc,
  SortDesc,
  Grid3x3,
  List,
  Star,
  TrendingUp,
  Sparkles,
  X,
  ChevronDown,
  Heart
} from "lucide-react";
import IntegrationDetailModal from "./IntegrationDetailModal";
import UnifiedAppsPanel from "@/components/apps/UnifiedAppsPanel";

interface IntegrationListItem {
  type: IntegrationType;
  name: string;
  description: string;
  icon: string;
  category: string;
  capability: Capability;
  isOAuth: boolean;
  isFree: boolean;
  price?: number;
  isConnected: boolean;
  isFavorite?: boolean;
}

type SortOption = "name" | "price-low" | "price-high" | "category" | "recent";
type ViewMode = "list" | "grid";

export default function UnifiedMarketplace() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { profile, loading: themeLoading } = useTheme();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationListItem | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>("name");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [showFilters, setShowFilters] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [recentlyViewed, setRecentlyViewed] = useState<string[]>([]);
  const [filters, setFilters] = useState({
    pricingBand: "all" as "all" | "micro" | "power" | "authority",
    subscriptionTier: "all" as "all" | "free" | "personal" | "pro" | "business",
    riskLevel: "all" as "all" | "low" | "medium" | "high",
    connectionStatus: "all" as "all" | "connected" | "not-connected",
    oauthOnly: false,
    freeOnly: false,
  });
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 50 }); // Virtual scrolling
  const listContainerRef = useRef<HTMLDivElement>(null);

  const AVAILABLE_INTEGRATIONS_REF = useRef<AvailableIntegrationRow[] | null>(null);
  useEffect(() => {
    if (AVAILABLE_INTEGRATIONS_REF.current === null) {
      try {
        AVAILABLE_INTEGRATIONS_REF.current = getAvailableIntegrationsFromCapabilities();
      } catch (error) {
        console.error("[Marketplace] Failed to load integrations:", error);
        AVAILABLE_INTEGRATIONS_REF.current = [];
      }
    }
  }, []);
  
  const AVAILABLE_INTEGRATIONS = AVAILABLE_INTEGRATIONS_REF.current || [];

  useEffect(() => {
    loadIntegrations();
    loadFavorites();
    loadRecentlyViewed();
  }, []);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load favorites from localStorage
  function loadFavorites() {
    if (typeof window === "undefined") return;
    try {
      const saved = localStorage.getItem("marketplace_favorites");
      if (saved) {
        setFavorites(new Set(JSON.parse(saved)));
      }
    } catch (error) {
      console.error("Failed to load favorites:", error);
    }
  }

  // Load recently viewed from localStorage
  function loadRecentlyViewed() {
    if (typeof window === "undefined") return;
    try {
      const saved = localStorage.getItem("marketplace_recently_viewed");
      if (saved) {
        setRecentlyViewed(JSON.parse(saved));
      }
    } catch (error) {
      console.error("Failed to load recently viewed:", error);
    }
  }

  // Save favorite
  function toggleFavorite(integrationType: string) {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(integrationType)) {
      newFavorites.delete(integrationType);
    } else {
      newFavorites.add(integrationType);
    }
    setFavorites(newFavorites);
    if (typeof window !== "undefined") {
      localStorage.setItem("marketplace_favorites", JSON.stringify(Array.from(newFavorites)));
    }
  }

  // Track recently viewed
  function trackRecentlyViewed(integrationType: string) {
    const updated = [integrationType, ...recentlyViewed.filter(id => id !== integrationType)].slice(0, 10);
    setRecentlyViewed(updated);
    if (typeof window !== "undefined") {
      localStorage.setItem("marketplace_recently_viewed", JSON.stringify(updated));
    }
  }

  // Handle OAuth callback
  useEffect(() => {
    if (typeof window === "undefined" || loading) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const connected = urlParams.get("connected");
    const token = urlParams.get("token");
    const error = urlParams.get("error");
    
    if (connected && token) {
      (async () => {
        try {
          const tokenData = JSON.parse(decodeURIComponent(token));
          const integrationType = connected as IntegrationType;
          
          // Save the integration
          const existing = integrations.find((i) => i.type === integrationType);
          const integration = (AVAILABLE_INTEGRATIONS_REF.current || []).find((i) => i.type === integrationType);
          
          if (existing) {
            await db.integrations.update(existing.id, {
              config: {
                access_token: tokenData.access_token,
                refresh_token: tokenData.refresh_token,
                expires_in: tokenData.expires_in,
                token_type: tokenData.token_type || "Bearer",
              },
              enabled: true,
              updatedAt: Date.now(),
            });
          } else if (integration) {
            await db.integrations.add({
              id: crypto.randomUUID(),
              type: integrationType,
              name: integration.name,
              description: integration.description,
              icon: integration.icon,
              enabled: true,
              config: {
                access_token: tokenData.access_token,
                refresh_token: tokenData.refresh_token,
                expires_in: tokenData.expires_in,
                token_type: tokenData.token_type || "Bearer",
              },
              createdAt: Date.now(),
              updatedAt: Date.now(),
              userId: getCurrentUserId(),
            });
          }
          
          await loadIntegrations();
          window.history.replaceState({}, "", "/marketplace");
        } catch (err) {
          console.error("Failed to save OAuth integration:", err);
          alert("Failed to save integration. Please try again.");
        }
      })();
    }
    
    if (error) {
      alert(`OAuth connection failed: ${error}`);
      window.history.replaceState({}, "", "/marketplace");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  async function loadIntegrations() {
    try {
      if (typeof window === "undefined" || !db || !db.integrations) {
        setLoading(false);
        return;
      }
      const all = await getIntegrationsForCurrentUser();
      setIntegrations(all);
    } catch (error) {
      console.error("Failed to load integrations:", error);
    } finally {
      setLoading(false);
    }
  }

  // Create integration list items with OAuth/price info
  const integrationList: IntegrationListItem[] = useMemo(() => {
    let integrationsList = AVAILABLE_INTEGRATIONS_REF.current || [];
    
    // If ref is empty, try to get integrations directly
    if (integrationsList.length === 0) {
      try {
        integrationsList = getAvailableIntegrationsFromCapabilities();
        AVAILABLE_INTEGRATIONS_REF.current = integrationsList;
      } catch (error) {
        console.error("[Marketplace] Failed to get integrations:", error);
        return [];
      }
    }
    
    // Map all integrations to list items — every plugin has OAuth or API key via getIntegrationAuthConfig
    return integrationsList.map((integration) => {
      const authConfig = getIntegrationAuthConfig(integration.type);
      const isOAuth = authConfig.supportsOAuth || authConfig.method === "oauth" || authConfig.method === "both";
      const existing = integrations.find((i) => i.type === integration.type);
      const isConnected = existing && Object.keys(existing.config || {}).length > 0;
      // Free = no purchase required ($0 or no unlock price). All 200+ can "Connect" via OAuth or API key.
      const isFree = !integration.capability.unlockPriceUSD || integration.capability.unlockPriceUSD === 0;
      const price = isFree ? undefined : integration.capability.unlockPriceUSD;

      return {
        type: integration.type,
        name: integration.name,
        description: integration.description,
        icon: integration.icon,
        category: integration.category,
        capability: integration.capability,
        isOAuth,
        isFree,
        price,
        isConnected,
        isFavorite: favorites.has(integration.type),
      };
    });
  }, [integrations, favorites]);

  // Get featured integrations (Phase 1 capabilities)
  const featuredIntegrations = useMemo(() => {
    return integrationList
      .filter(item => item.capability?.phase === 1)
      .slice(0, 6);
  }, [integrationList]);

  // Get recommended integrations based on connected ones
  const recommendedIntegrations = useMemo(() => {
    const connectedCategories = new Set(
      integrationList
        .filter(item => item.isConnected)
        .map(item => item.category)
    );
    
    return integrationList
      .filter(item => 
        !item.isConnected && 
        connectedCategories.has(item.category)
      )
      .slice(0, 6);
  }, [integrationList]);

  // Reset visible range when filters/search change
  useEffect(() => {
    setVisibleRange({ start: 0, end: 50 });
  }, [selectedCategory, debouncedSearchQuery, sortBy, filters]);

  // Filter and sort integrations
  const filteredAndSortedIntegrations = useMemo(() => {
    let filtered = integrationList;

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter((item) => item.category === selectedCategory);
    }

    // Filter by debounced search
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query) ||
          item.category.toLowerCase().includes(query) ||
          item.capability?.pricingBand?.toLowerCase().includes(query) ||
          item.capability?.actionTypes?.some(t => t.toLowerCase().includes(query))
      );
    }

    // Advanced filters
    if (filters.pricingBand !== "all") {
      filtered = filtered.filter((item) => item.capability?.pricingBand === filters.pricingBand);
    }

    if (filters.subscriptionTier !== "all") {
      filtered = filtered.filter((item) => 
        !item.capability?.requiredSubscriptionTier || 
        item.capability.requiredSubscriptionTier === filters.subscriptionTier
      );
    }

    if (filters.riskLevel !== "all") {
      filtered = filtered.filter((item) => item.capability?.riskLevel === filters.riskLevel);
    }

    if (filters.connectionStatus === "connected") {
      filtered = filtered.filter((item) => item.isConnected);
    } else if (filters.connectionStatus === "not-connected") {
      filtered = filtered.filter((item) => !item.isConnected);
    }

    if (filters.oauthOnly) {
      filtered = filtered.filter((item) => item.isOAuth);
    }

    if (filters.freeOnly) {
      filtered = filtered.filter((item) => item.isFree);
    }

    // Sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "price-low":
          return (a.price || 0) - (b.price || 0);
        case "price-high":
          return (b.price || 0) - (a.price || 0);
        case "category":
          return a.category.localeCompare(b.category);
        case "recent":
          const phaseOrder = { 1: 0, 2: 1, future: 2 };
          const aPhase = a.capability?.phase || "future";
          const bPhase = b.capability?.phase || "future";
          return (phaseOrder[aPhase as keyof typeof phaseOrder] || 2) - (phaseOrder[bPhase as keyof typeof phaseOrder] || 2);
        default:
          return 0;
      }
    });

    return sorted;
  }, [integrationList, selectedCategory, debouncedSearchQuery, sortBy, filters]);

  // Stats
  const stats = useMemo(() => {
    return {
      total: integrationList.length,
      connected: integrationList.filter(i => i.isConnected).length,
      free: integrationList.filter(i => i.isFree).length,
      oauth: integrationList.filter(i => i.isOAuth).length,
      favorites: favorites.size,
    };
  }, [integrationList, favorites]);

  // Get unique categories
  const categories = useMemo(() => {
    const integrationsList = AVAILABLE_INTEGRATIONS_REF.current || [];
    const cats = new Set(integrationsList.map((item) => item.category));
    return Array.from(cats).sort();
  }, []);

  if (themeLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pb-24">
        <div className="text-slate-600 dark:text-slate-400">Loading...</div>
      </div>
    );
  }

  const primaryColor = profile?.brand?.primaryColor || "#6366f1";
  const secondaryColor = profile?.brand?.secondaryColor || "#8b5cf6";
  const textColor = "#f1f5f9";
  const textSecondaryColor = "rgba(241, 245, 249, 0.75)";

  return (
    <div
      className="min-h-screen p-4 md:p-8 relative overflow-hidden pb-24 font-sans bg-gray-900"
      style={{
        background: "linear-gradient(180deg, #0f172a 0%, #1e293b 40%, #0f172a 100%)",
      }}
    >
      {/* Subtle accent glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-40 -right-40 w-80 h-80 rounded-full filter blur-3xl opacity-20"
          style={{ backgroundColor: primaryColor }}
        />
        <div
          className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full filter blur-3xl opacity-15 animation-delay-2000"
          style={{ backgroundColor: secondaryColor }}
        />
      </div>

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Header with Stats — Cursor-style: clean "Plugins" marketplace */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2 text-slate-50">Plugins</h1>
              <p className="text-sm text-slate-400">Extend your assistant with plugins. Connect with OAuth or API key—simple and easy.</p>
            </div>
            {/* Quick Stats */}
            <div className="hidden md:flex items-center gap-4 text-sm">
              <div className="text-center">
                <div className="font-bold text-slate-100">{stats.total}</div>
                <div className="text-xs text-slate-400">Total</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-emerald-400">{stats.connected}</div>
                <div className="text-xs text-slate-400">Connected</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-indigo-400">{stats.free}</div>
                <div className="text-xs text-slate-400">Free</div>
              </div>
              {stats.favorites > 0 && (
                <div className="text-center">
                  <div className="font-bold text-amber-400">{stats.favorites}</div>
                  <div className="text-xs text-slate-400">Favorites</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Your apps + Discover (power-ups + integrations) — same panel as Builder */}
        <UnifiedAppsPanel
          variant="full"
          primaryColor={primaryColor}
          textColor={textColor}
          textSecondaryColor={textSecondaryColor}
        />

        {/* Search and Controls Bar + full app grid */}
        <div id="browse" className="mb-4 space-y-3 scroll-mt-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search plugins..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-3 rounded-xl border border-slate-600 bg-slate-800/90 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-slate-100"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-black/10 transition"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            )}
          </div>

          {/* Controls Row */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* View Toggle */}
            <div className="flex items-center gap-1 p-1 rounded-lg bg-slate-800/80 border border-slate-600/50">
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded transition ${
                  viewMode === "list"
                    ? "text-white"
                    : "hover:bg-black/10"
                }`}
                style={
                  viewMode === "list"
                    ? {
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                      }
                    : { color: textColor }
                }
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded transition ${
                  viewMode === "grid"
                    ? "text-white"
                    : "hover:bg-black/10"
                }`}
                style={
                  viewMode === "grid"
                    ? {
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                      }
                    : { color: textColor }
                }
              >
                <Grid3x3 className="w-4 h-4" />
              </button>
            </div>

            {/* Sort Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/80 border border-slate-600/50 text-sm font-medium text-slate-200 transition hover:bg-slate-700/80"
              >
                <SortAsc className="w-4 h-4" />
                {sortBy === "name" && "Name"}
                {sortBy === "price-low" && "Price ↑"}
                {sortBy === "price-high" && "Price ↓"}
                {sortBy === "category" && "Category"}
                {sortBy === "recent" && "Recent"}
                <ChevronDown className="w-4 h-4" />
              </button>
              {showFilters && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowFilters(false)}
                  />
                  <div className="absolute top-full left-0 mt-2 w-48 rounded-lg bg-slate-800 border border-slate-600 shadow-xl z-50">
                    {(["name", "price-low", "price-high", "category", "recent"] as SortOption[]).map((option) => (
                      <button
                        key={option}
                        onClick={() => {
                          setSortBy(option);
                          setShowFilters(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition text-slate-200 hover:bg-slate-700/80 ${sortBy === option ? "font-semibold text-indigo-300" : ""}`}
                      >
                        {option === "name" && "Name (A-Z)"}
                        {option === "price-low" && "Price (Low to High)"}
                        {option === "price-high" && "Price (High to Low)"}
                        {option === "category" && "Category"}
                        {option === "recent" && "Recently Added"}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/80 border border-slate-600/50 text-sm font-medium text-slate-200 transition hover:bg-slate-700/80"
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>

            {/* Quick Filters */}
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={() => setFilters({ ...filters, freeOnly: !filters.freeOnly })}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  filters.freeOnly ? "text-white" : "bg-slate-800/80 border border-slate-600/50 text-slate-200"
                }`}
                style={filters.freeOnly ? { background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` } : undefined}
              >
                Free Only
              </button>
              <button
                onClick={() => setFilters({ ...filters, oauthOnly: !filters.oauthOnly })}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  filters.oauthOnly ? "text-white" : "bg-slate-800/80 border border-slate-600/50 text-slate-200"
                }`}
                style={filters.oauthOnly ? { background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` } : undefined}
              >
                OAuth
              </button>
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="p-4 rounded-xl bg-slate-800/90 border border-slate-600">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs font-medium mb-2 block text-slate-400">Pricing Band</label>
                  <select
                    value={filters.pricingBand}
                    onChange={(e) => setFilters({ ...filters, pricingBand: e.target.value as any })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-600 text-sm bg-slate-700 text-slate-100"
                  >
                    <option value="all">All</option>
                    <option value="micro">Micro ($4.99)</option>
                    <option value="power">Power ($9.99-$19.99)</option>
                    <option value="authority">Authority ($29+)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium mb-2 block text-slate-400">Subscription Tier</label>
                  <select
                    value={filters.subscriptionTier}
                    onChange={(e) => setFilters({ ...filters, subscriptionTier: e.target.value as any })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-600 text-sm bg-slate-700 text-slate-100"
                  >
                    <option value="all">All</option>
                    <option value="free">Free</option>
                    <option value="personal">Personal</option>
                    <option value="pro">Pro</option>
                    <option value="business">Business</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium mb-2 block text-slate-400">Risk Level</label>
                  <select
                    value={filters.riskLevel}
                    onChange={(e) => setFilters({ ...filters, riskLevel: e.target.value as any })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-600 text-sm bg-slate-700 text-slate-100"
                  >
                    <option value="all">All</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium mb-2 block text-slate-400">Status</label>
                  <select
                    value={filters.connectionStatus}
                    onChange={(e) => setFilters({ ...filters, connectionStatus: e.target.value as any })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-600 text-sm bg-slate-700 text-slate-100"
                  >
                    <option value="all">All</option>
                    <option value="connected">Connected</option>
                    <option value="not-connected">Not Connected</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Category Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-thin">
          <button
            onClick={() => setSelectedCategory("all")}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${
              selectedCategory === "all" ? "text-white" : "bg-slate-800/80 border border-slate-600/50 text-slate-200 hover:bg-slate-700/80"
            }`}
            style={selectedCategory === "all" ? { background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` } : undefined}
          >
            All
          </button>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${
                selectedCategory === category ? "text-white" : "bg-slate-800/80 border border-slate-600/50 text-slate-200 hover:bg-slate-700/80"
              }`}
              style={selectedCategory === category ? { background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` } : undefined}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Featured Section */}
        {featuredIntegrations.length > 0 && selectedCategory === "all" && !debouncedSearchQuery && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-indigo-400" />
              <h2 className="text-xl font-bold text-slate-100">Featured</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {featuredIntegrations.map((item) => (
                <button
                  key={`featured-${item.type}`}
                  onClick={() => {
                    trackRecentlyViewed(item.type);
                    setSelectedIntegration(item);
                  }}
                  className="p-3 rounded-xl bg-slate-800/60 border border-slate-600/50 hover:bg-slate-700/60 transition text-center"
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl mx-auto mb-2 bg-slate-700/80"
                    style={{ background: item.isConnected ? `linear-gradient(135deg, ${primaryColor}30 0%, ${secondaryColor}30 100%)` : undefined }}
                  >
                    <ProductIcon icon={item.icon} size={32} />
                  </div>
                  <h3 className="font-semibold text-xs mb-1 text-slate-100">{item.name}</h3>
                  {item.isFree && (
                    <span className="text-xs px-2 py-0.5 rounded text-white" style={{ background: primaryColor }}>Free</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations Section */}
        {recommendedIntegrations.length > 0 && selectedCategory === "all" && !debouncedSearchQuery && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-indigo-400" />
              <h2 className="text-xl font-bold text-slate-100">Recommended for You</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {recommendedIntegrations.map((item) => (
                <button
                  key={`recommended-${item.type}`}
                  onClick={() => {
                    trackRecentlyViewed(item.type);
                    setSelectedIntegration(item);
                  }}
                  className="p-3 rounded-xl bg-slate-800/60 border border-slate-600/50 hover:bg-slate-700/60 transition text-center"
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl mx-auto mb-2 bg-slate-700/80">
                    <ProductIcon icon={item.icon} size={32} />
                  </div>
                  <h3 className="font-semibold text-xs mb-1 text-slate-100">{item.name}</h3>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results Counter */}
        <div className="mb-4 text-sm flex items-center justify-between text-slate-400">
          <span>
            Showing {Math.min(visibleRange.end, filteredAndSortedIntegrations.length)} of {filteredAndSortedIntegrations.length} plugins
            {filteredAndSortedIntegrations.length < integrationList.length && (
              <span className="ml-2">(filtered from {integrationList.length} plugins)</span>
            )}
          </span>
          {favorites.size > 0 && (
            <button
              onClick={() => {
                const favoriteItems = integrationList.filter(item => favorites.has(item.type));
                if (favoriteItems.length > 0) {
                  setSelectedCategory("all");
                  setSearchQuery("");
                  // Scroll to favorites section or filter by favorites
                }
              }}
              className="flex items-center gap-1 text-sm text-indigo-400"
            >
              <Heart className="w-4 h-4 fill-current" />
              {favorites.size} Favorites
            </button>
          )}
        </div>

        {/* Integration List/Grid */}
        {filteredAndSortedIntegrations.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p className="text-lg mb-2 text-slate-200">No plugins found</p>
            <p className="text-sm">Try adjusting your filters or search query</p>
          </div>
        ) : viewMode === "list" ? (
          <div className="space-y-1">
            {filteredAndSortedIntegrations.slice(visibleRange.start, visibleRange.end).map((item) => (
              <div
                key={`list-${item.type}`}
                role="button"
                tabIndex={0}
                onClick={() => {
                  trackRecentlyViewed(item.type);
                  setSelectedIntegration(item);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    trackRecentlyViewed(item.type);
                    setSelectedIntegration(item);
                  }
                }}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-slate-800/50 border border-slate-600/40 hover:bg-slate-700/50 transition text-left group cursor-pointer"
              >
                {/* Icon */}
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-700/80">
                  <ProductIcon icon={item.icon} size={32} />
                </div>

                {/* Name and Description */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <h3 className="font-semibold text-sm truncate text-slate-100">{item.name}</h3>
                    {item.isConnected && (
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    )}
                    {item.isFree && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium text-white" style={{ background: primaryColor }}>
                        Free
                      </span>
                    )}
                    {item.capability?.pricingBand && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-slate-200 capitalize">{item.capability.pricingBand}</span>
                    )}
                    {item.capability?.phase === 1 && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200">
                        New
                      </span>
                    )}
                  </div>
                  <p className="text-xs line-clamp-1 truncate text-slate-400">{item.description}</p>
                </div>

                {/* Favorite Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(item.type);
                  }}
                  className="opacity-0 group-hover:opacity-100 transition p-1 rounded hover:bg-black/10"
                >
                  <Heart 
                    className={`w-4 h-4 ${item.isFavorite ? "fill-current text-yellow-500" : ""}`}
                    style={{ color: item.isFavorite ? undefined : textSecondaryColor }}
                  />
                </button>

                {/* Price or Connect Badge */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {item.isFree ? (
                    <div
                      className="px-3 py-1 rounded-lg text-xs font-medium text-white"
                      style={{
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                      }}
                    >
                      Connect
                    </div>
                  ) : item.price ? (
                    <div className="text-sm font-medium" style={{ color: textColor }}>
                      ${item.price}
                    </div>
                  ) : null}
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {filteredAndSortedIntegrations.slice(visibleRange.start, visibleRange.end).map((item) => (
              <div
                key={`grid-${item.type}`}
                role="button"
                tabIndex={0}
                onClick={() => {
                  trackRecentlyViewed(item.type);
                  setSelectedIntegration(item);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    trackRecentlyViewed(item.type);
                    setSelectedIntegration(item);
                  }
                }}
                className="p-4 rounded-xl bg-slate-800/50 border border-slate-600/40 hover:bg-slate-700/50 transition text-left flex flex-col relative group cursor-pointer"
              >
                {/* Favorite Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(item.type);
                  }}
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition p-1 rounded hover:bg-black/10 z-10"
                >
                  <Heart 
                    className={`w-4 h-4 ${item.isFavorite ? "fill-current text-yellow-500" : ""}`}
                    style={{ color: item.isFavorite ? undefined : textSecondaryColor }}
                  />
                </button>

                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 mx-auto bg-slate-700/80">
                  <ProductIcon icon={item.icon} size={48} />
                </div>
                <h3 className="font-semibold text-sm mb-1 text-center text-slate-100">{item.name}</h3>
                <p className="text-xs line-clamp-2 mb-2 text-center text-slate-400">{item.description}</p>
                <div className="flex items-center justify-center gap-1 flex-wrap mt-auto">
                  {item.isConnected && (
                    <Check className="w-3 h-3 text-green-600" />
                  )}
                  {item.isFree && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium text-white" style={{ background: primaryColor }}>
                      Free
                    </span>
                  )}
                  {item.capability?.pricingBand && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-slate-200 capitalize">{item.capability.pricingBand}</span>
                  )}
                </div>
                {item.isFree ? (
                  <div
                    className="px-3 py-1 rounded-lg text-xs font-medium text-white mt-2 text-center"
                    style={{
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                    }}
                  >
                    Connect
                  </div>
                ) : item.price ? (
                  <div className="text-sm font-medium mt-2 text-center" style={{ color: textColor }}>
                    ${item.price}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}

        {/* Load More Button for Virtual Scrolling */}
        {visibleRange.end < filteredAndSortedIntegrations.length && (
          <div className="text-center mt-6">
            <button
              onClick={() => setVisibleRange(prev => ({
                start: 0,
                end: Math.min(prev.end + 50, filteredAndSortedIntegrations.length)
              }))}
              className="px-6 py-2 rounded-lg text-sm font-medium transition hover:scale-105"
              style={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                color: "white",
              }}
            >
              Load More ({filteredAndSortedIntegrations.length - visibleRange.end} remaining)
            </button>
          </div>
        )}
      </div>

      {/* Integration Detail Modal */}
      {selectedIntegration && (
        <IntegrationDetailModal
          integration={selectedIntegration}
          connectedIntegration={integrations.find((i) => i.type === selectedIntegration.type)}
          onClose={() => setSelectedIntegration(null)}
          onConnect={async (integrationType: IntegrationType, config: Record<string, any>) => {
            try {
              const existing = integrations.find((i) => i.type === integrationType);
              const integration = (AVAILABLE_INTEGRATIONS_REF.current || []).find((i) => i.type === integrationType);
              
              if (existing) {
                await db.integrations.update(existing.id, {
                  config,
                  enabled: true,
                  updatedAt: Date.now(),
                });
              } else if (integration) {
                await db.integrations.add({
                  id: crypto.randomUUID(),
                  type: integrationType,
                  name: integration.name,
                  description: integration.description,
                  icon: integration.icon,
                  enabled: true,
                  config,
                  createdAt: Date.now(),
                  updatedAt: Date.now(),
                  userId: getCurrentUserId(),
                });
              }
              
              await loadIntegrations();
              setSelectedIntegration(null);
            } catch (error) {
              console.error("Failed to connect integration:", error);
              throw error;
            }
          }}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          textColor={textColor}
          textSecondaryColor={textSecondaryColor}
        />
      )}
    </div>
  );
}
